import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { posService } from "../../../../services/pos.service";
import { paymentValidateRequest, paymentValidateResponse } from "../../types/pos.types";
import { LoadingState } from "../../components/LoadingState/LoadingState";
// Types for state
type ValidationStatus = "loading" | "success" | "error";

export const SuccessfulPaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const trackId = searchParams.get("track_id");

  const [validationStatus, setValidationStatus] = useState<ValidationStatus>("loading");
  const [paymentData, setPaymentData] = useState<paymentValidateResponse | null>(null);

  useEffect(() => {
    const validate = async () => {
      if (!trackId) {
        setValidationStatus("error");
        return;
      }

      try {
        const result = await posService.validatePayment({ track_id: trackId });
        if (result.success && result.data?.success) {
          setValidationStatus("success");
          setPaymentData(result.data);
        } else {
          setValidationStatus("error");
        }
      } catch (err) {
        console.error("Payment validation failed:", err);
        setValidationStatus("error");
      }
    };

    validate();
  }, [trackId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all hover:shadow-3xl duration-500 ease-in-out">
        {/* Conditional Content */}
        {validationStatus === "loading" && (
          <div className="p-12">
            <LoadingState message="Verifying your payment..." />
          </div>
        )}

        {validationStatus === "success" && (
          <>

            {/* Success UI */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white text-center relative">
              <div className="absolute inset-0 opacity-10">
                {/* SVG Background */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="h-full w-full">
                  <path
                    fill="currentColor"
                    fillOpacity="1"
                    d="M0,96L48,112C96,128,192,160,288,176C384,192,480,192,576,176C672,160,768,128,864,122.7C960,117,1056,139,1152,144C1248,149,1344,139,1392,133L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
                  />
                </svg>
              </div>

              {/* Icon */}
              <div className="mb-4 flex justify-center animate-bounce">
                <div className="h-20 w-20 rounded-full bg-white text-green-500 flex items-center justify-center shadow-lg border-4 border-green-200">
                  <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold mb-2">Payment Successful!</h1>
              <p className="text-green-100 text-sm sm:text-base">Thank you for choosing us.</p>

              {/* Track ID Display */}
              {trackId && (
                <p className="mt-4 text-green-100 text-sm font-mono">
                  Transaction ID: <strong>{trackId}</strong>
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-100 px-8 py-4 text-center text-xs text-gray-500 border-t border-gray-200">
              © {new Date().getFullYear()} VenFlows. All rights reserved.
            </div>
          </>
        )}

        {validationStatus === "error" && (
          <div className="p-12 text-center">
            <div className="mb-4 flex justify-center">
              <div className="h-16 w-16 rounded-full bg-red-100 text-red-500 flex items-center justify-center">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-red-600 mb-2">Payment Verification Failed</h2>
            <p className="text-gray-600">
              We couldn't verify your payment status. Please try again or contact support.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
